import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { onCopyTaskCallback } from 'donor-elf-web/components/task/info';
import dateUtils from 'donor-elf-web/lib/date-utils';
import {
  JournalRow,
  JournalStageColumn,
  type onJournalCellChangeCallback,
} from 'donor-elf-web/lib/journal-utils';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import TaskService from 'donor-elf-web/services/task-service';
import moment from 'moment';

interface JournalCellStageIndexComponentArgs {
  journalRow: JournalRow;
  journalStageColumn: JournalStageColumn;
  taskToPaste?: V2TaskModel;
  showAssignedUsersIfPresent: boolean;
  onCopyTask: onCopyTaskCallback;
  onChange: onJournalCellChangeCallback;
}

export default class JournalCellStageIndexComponent extends Component<JournalCellStageIndexComponentArgs> {
  @service declare fundsService: any;
  @service declare taskService: TaskService;

  tdElement: any;
  @tracked showCompletedTasks = false;

  @action
  hideCheck() {
    this.showCompletedTasks = true;
  }

  get showCheckForCompletedTasks() {
    return (
      this.completedTasks.length > 0 &&
      !this.args.journalRow.showTasks &&
      !this.showCompletedTasks
    );
  }

  get completedTasks() {
    return this.stageTasks.filter((x) => x.isComplete);
  }

  get allTasksCompleted() {
    if ((this.stageTasks?.length || 0) === 0) {
      return false;
    }
    return this.stageTasks.find((x) => !x.isComplete) == null;
  }

  get allCompletedTasksAttempted() {
    let { completedTasks } = this;
    return (
      completedTasks.length === 0 ||
      completedTasks.find((x) => !x.isAttemptedResult) == null
    );
  }

  @action
  templateDidInsert(el: any) {
    this.tdElement = el.closest('td');
    this.tdElement.addEventListener('click', this.onTdCellClicked);
  }

  @action
  templateWillDestroy() {
    this.tdElement.removeEventListener('click', this.onTdCellClicked);
  }

  get stageId() {
    return this.args.journalStageColumn.stageId;
  }

  get stageTasks() {
    return this.args.journalRow.journalContactInfo.tasksByStageId[this.stageId];
  }

  get stageTaskInfos() {
    return this.args.journalRow.taskInfosByStageId[this.stageId];
  }

  get stageTaskInfosToShow() {
    return this.showCheckForCompletedTasks
      ? this.stageTaskInfos.filter((x) => !x.task.isComplete)
      : this.stageTaskInfos;
  }

  get journalContact() {
    return this.args.journalRow.journalContactInfo.journalContact;
  }

  get cellState() {
    return this.args.journalRow.cellState[this.stageId];
  }

  get currentStageButNoOpenTasks() {
    return (
      this.args.journalStageColumn.stageIndex ===
        this.args.journalRow.journalContactInfo.currentStageIndex &&
      this.stageTasks.find((x) => !x.isComplete) == null &&
      !this.args.journalRow.journalContactInfo.journalContact.finished
    );
  }

  get showAddDecision() {
    return this.cellState.showAddButton;
  }

  get showAddTask() {
    return (
      this.cellState.showAddButton ||
      (this.currentStageButNoOpenTasks && this.cellState.taskIsSaving !== true)
    );
  }

  get showQuickAddAttemptedButton() {
    return this.args.journalStageColumn.showAttemptedQuickAddButton;
  }

  get showQuickAddCompletedButton() {
    return this.args.journalStageColumn.showCompletedQuickAddButton;
  }

  get showPasteTask() {
    return this.args.taskToPaste != null;
  }

  get newTask() {
    return this.cellState.newTask;
  }

  @action
  onTdCellClicked(e: any) {
    e.stopImmediatePropagation();
    this.cellState.showAddButton = !this.cellState.showAddButton;
    if (this.showAddTask === false && this.showCompletedTasks) {
      this.showCompletedTasks = false;
    }
    this.args.journalRow.fireCellStateChanged();
  }

  @action
  handleAddTask(dropDown: any, e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (this.cellState.savingQuickAddTask) {
      return;
    }

    if (this.cellState.newTask == null) {
      this.cellState.newTask = this.taskService.createRecord({
        contactId: this.args.journalRow.journalContactInfo.contactId,
        journalStageId: this.args.journalStageColumn.stageId,
      });
      if (this.args.journalStageColumn.isFollowUpColumn) {
        this.cellState.newTask.dueDate = moment().add(2, 'days').toDate();
      }
      this.args.journalRow.fireCellStateChanged();
    }
    dropDown.actions.open();
  }

  @action
  handleQuickAddTask(
    resultLabel: 'Completed' | 'Attempted',
    dropDown: any,
    e: any,
  ) {
    e.preventDefault();
    e.stopPropagation();
    if (this.cellState.savingQuickAddTask) {
      return;
    }

    this.cellState.savingQuickAddTask = true;
    let result = this.taskService.results.find((x) => x.label === resultLabel);
    this.cellState.newTask = this.taskService.createRecord({
      contactId: this.args.journalRow.journalContactInfo.contactId,
      journalStageId: this.args.journalStageColumn.stageId,
      actionId: this.args.journalStageColumn.lastTaskActionId,
      mediumId: this.args.journalStageColumn.lastTaskMediumId,
      resultId: result?.id,
      completedDate: dateUtils.correctTimezone(new Date()),
    });
    this.args.journalRow.fireCellStateChanged();

    dropDown.actions.open();
  }

  get contactTasks() {
    return this.args.journalRow.journalContactInfo.tasks;
  }

  @action
  handleTaskSaving({ saving, task }: { saving: boolean; task: V2TaskModel }) {
    if (saving) {
      let taskInfo = this.args.journalRow.taskInfosById[task.id];
      if (taskInfo && taskInfo.errorSavingTask) {
        taskInfo.errorSavingTask = false;
      }
    }
    if (this.cellState.taskIsSaving !== saving) {
      if (!saving || !task.isNew) {
        this.cellState.taskIsSaving = saving;
        this.args.journalRow.fireCellStateChanged();
      }
    }
  }

  @action
  async handleTaskSaveError({ task }: { task: V2TaskModel; error: any }) {
    this.args.journalRow.journalContactInfo.recalculate();
    let taskInfo = this.args.journalRow.taskInfosById[task.id];
    if (taskInfo) {
      taskInfo.errorSavingTask = true;
    }
    if (this.cellState.savingQuickAddTask) {
      this.cellState.savingQuickAddTask = false;
      this.args.journalRow.fireCellStateChanged();
    }
  }

  @action
  handleTaskSaved(task: V2TaskModel) {
    if (this.contactTasks.find((x) => x.id === task.id) == null) {
      this.contactTasks.push(task);
    }
    if (this.cellState.savingQuickAddTask) {
      this.showCompletedTasks = true;
      this.cellState.savingQuickAddTask = false;
    } else {
      this.showCompletedTasks = false;
    }
    this.cellState.showAddButton = false;
    this.cellState.newTask = null;
    this.args.journalRow.fireCellStateChanged();
    this.args.journalRow.journalContactInfo.recalculate();
    if (
      this.args.journalStageColumn.lastTaskActionId != task.actionId ||
      this.args.journalStageColumn.lastTaskMediumId != task.mediumId
    ) {
      this.args.journalStageColumn.lastTaskActionId = task.actionId;
      this.args.journalStageColumn.lastTaskMediumId = task.mediumId;
      this.args.journalStageColumn.calculateShowQuickAddButtons();
    }
    this.args.onChange({ taskSaved: task });
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this.args.journalRow.journalContactInfo.tasks = this.contactTasks.filter(
      (x) => x.id !== task.id,
    );
    this.args.journalRow.journalContactInfo.recalculate();
    this.args.onChange({ taskDeleted: task });
  }

  @action
  async handlePasteTask(e: any) {
    e.preventDefault();
    e.stopPropagation();
    this.cellState.savingTaskToPaste = true;
    this.args.journalRow.fireCellStateChanged();
    let taskToPaste = this.args.taskToPaste!;
    let task = this.taskService.copyRecord(taskToPaste);
    task.contactId = this.args.journalRow.journalContactInfo.contactId;
    task.journalStageId = this.args.journalStageColumn.stageId;
    try {
      await task.save();
    } catch (error) {
      this.cellState.savingTaskToPaste = false;
      this.cellState.errorSavingPastedTask = true;
      this.args.journalRow.fireCellStateChanged();
      return;
    }

    this.contactTasks.push(task);
    this.showCompletedTasks = true;
    this.args.journalRow.journalContactInfo.recalculate();
    this.cellState.showAddButton = false;
    this.cellState.savingTaskToPaste = false;
    this.args.journalRow.fireCellStateChanged();
    this.args.onChange({ taskSaved: task, taskPasted: true });
  }

  @action
  handleDecisionChanged() {
    this.cellState.showAddButton = false;
    this.args.journalRow.fireCellStateChanged();
    this.args.onChange({ decisionChanged: true });
  }

  @action
  async handleToggleFinished() {
    this.journalContact.finished = !this.journalContact.finished;
    await this.fundsService.confirmValidFundToken();
    await this.journalContact.save();
    this.args.journalRow.journalContactInfo.recalculate();
    this.args.onChange({});
  }
}
